import type { ApplyTravelerCaptureDto, TravelerResponseDto } from '@bandido/shared/dtos';
import { TRAVELER_CAPTURE_PROFILE_FIELDS } from '@bandido/shared/types';
import type { TravelerCaptureField, TravelerCaptureProfileField, TravelerCaptureProposal } from '@bandido/shared/types';

export const documentFields = ['type', 'number', 'issuingCountry', 'expiresOn'] as const;
export type DocumentField = (typeof documentFields)[number];
export interface ReviewField {
  selected: boolean;
  value: string;
}
export interface CaptureReviewValues {
  fields: Record<TravelerCaptureProfileField, ReviewField>;
  documents: Array<{ selected: boolean; id: string; fields: Record<DocumentField, ReviewField> }>;
}

const initialField = (field?: TravelerCaptureField, current?: string | null): ReviewField => ({
  value: field?.value ?? '',
  selected: Boolean(
    field?.value && !field.needsReview && !field.alternatives?.length && (!current || current === field.value),
  ),
});

/** Conflicts and uncertain readings are never preselected for an overwrite. */
export const captureReviewValues = (
  proposal: TravelerCaptureProposal,
  target?: TravelerResponseDto,
): CaptureReviewValues => ({
  fields: Object.fromEntries(
    TRAVELER_CAPTURE_PROFILE_FIELDS.map((key) => [
      key,
      {
        ...initialField(proposal.candidate.fields[key], target?.[key]),
        ...(key === 'birthDate' ? { selected: false } : {}),
      },
    ]),
  ) as CaptureReviewValues['fields'],
  documents: proposal.candidate.documents.map((document) => ({
    selected: false,
    id: '',
    fields: Object.fromEntries(
      documentFields.map((key) => [
        key,
        {
          ...initialField(document[key]),
          ...(key === 'expiresOn' ? { selected: false } : {}),
        },
      ]),
    ) as Record<DocumentField, ReviewField>,
  })),
});

export const buildCaptureApplication = (
  proposal: TravelerCaptureProposal,
  values: CaptureReviewValues,
  target?: TravelerResponseDto,
): ApplyTravelerCaptureDto => {
  const data: ApplyTravelerCaptureDto['data'] = {};
  for (const key of TRAVELER_CAPTURE_PROFILE_FIELDS) {
    if (values.fields[key].selected) {
      const value = values.fields[key].value.trim();
      if (key === 'firstName' || key === 'lastName') data[key] = value;
      else data[key] = value || null;
    }
  }
  const documents = values.documents.flatMap((document, index) => {
    if (!document.selected) return [];
    return [
      {
        ...(document.id ? { id: document.id } : {}),
        proposalDocumentIndex: index,
        type: document.fields.type.value.trim(),
        number: document.fields.number.value.trim(),
        ...(document.fields.issuingCountry.selected
          ? { issuingCountry: document.fields.issuingCountry.value.trim() || null }
          : {}),
        ...(document.fields.expiresOn.selected ? { expiresOn: document.fields.expiresOn.value.trim() || null } : {}),
      },
    ];
  });
  if (documents.length) data.documents = documents;
  return {
    version: proposal.version,
    action: target ? 'update' : 'create',
    ...(target ? { travelerId: target.id, expectedRevision: target.revision } : {}),
    data,
  };
};

export const captureApplicationReady = (input: ApplyTravelerCaptureDto) => {
  if (input.action === 'link') return Boolean(input.travelerId);
  if (input.action === 'create' && (!input.data.firstName?.trim() || !input.data.lastName?.trim())) return false;
  if (input.action === 'update' && (!input.expectedRevision || !Object.keys(input.data).length)) return false;
  return (input.data.documents ?? []).every((document) => Boolean(document.type && document.number));
};

/** A missing/5xx response can follow a committed write; retain the exact request. */
export const captureRetryMustKeepInput = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return !status || status >= 500;
};
